import { useEffect, useRef, useState } from "react";
import { Star, Camera, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

const BRAND = "#d81f26";

export default function ProductReviews({ productId }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState({ reviews: [], summary: { avg: 0, count: 0, distribution: {} } });
  const [form, setForm] = useState({ rating: 5, title: "", comment: "" });
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const fileRef = useRef(null);

  const load = () => api.get(`/products/${productId}/reviews`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, [productId]);

  const pickPhotos = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((f) => {
      const r = new FileReader();
      r.onload = () => setPhotos((p) => (p.length >= 4 ? p : [...p, r.result]));
      r.readAsDataURL(f);
    });
    e.target.value = "";
  };

  const submit = async () => {
    if (!user) { nav("/login"); return; }
    setBusy(true);
    try {
      await api.post(`/products/${productId}/reviews`, { ...form, photos });
      toast.success("Review submitted — dhanyavaad!");
      setForm({ rating: 5, title: "", comment: "" }); setPhotos([]);
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const s = data.summary || { avg: 0, count: 0, distribution: {} };
  return (
    <section className="mt-10" data-testid="product-reviews">
      <h2 className="text-xl font-bold mb-4">Ratings &amp; Reviews</h2>
      <div className="grid md:grid-cols-3 gap-6">
        <div className="bg-white border border-slate-100 rounded-2xl p-5 text-center h-fit">
          <p className="text-4xl font-extrabold" data-testid="reviews-avg">{s.avg || 0}</p>
          <div className="flex items-center justify-center gap-0.5 mt-1">{[1, 2, 3, 4, 5].map((i) => <Star key={i} size={16} className={i <= Math.round(s.avg) ? "fill-amber-400 text-amber-400" : "text-slate-200"} />)}</div>
          <p className="text-xs text-slate-400 mt-1" data-testid="reviews-count">{s.count} review{s.count === 1 ? "" : "s"}</p>
          <div className="mt-3 space-y-1">
            {[5, 4, 3, 2, 1].map((n) => {
              const c = (s.distribution || {})[String(n)] || 0;
              const pct = s.count ? Math.round((c / s.count) * 100) : 0;
              return (
                <div key={n} className="flex items-center gap-2 text-[11px] text-slate-500">
                  <span className="w-3">{n}</span><Star size={10} className="fill-amber-400 text-amber-400" />
                  <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-amber-400" style={{ width: `${pct}%` }} /></div>
                  <span className="w-6 text-right">{c}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="md:col-span-2">
          <div className="bg-white border border-slate-100 rounded-2xl p-4 mb-4" data-testid="review-form">
            <p className="font-bold text-sm mb-2">Write a review</p>
            <div className="flex items-center gap-1 mb-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <button key={i} type="button" data-testid={`review-star-${i}`} onClick={() => setForm({ ...form, rating: i })}>
                  <Star size={22} className={i <= form.rating ? "fill-amber-400 text-amber-400" : "text-slate-300"} />
                </button>
              ))}
            </div>
            <input data-testid="review-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title (optional)" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm mb-2" />
            <textarea data-testid="review-comment" value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} placeholder="Apna experience share karein…" rows={3} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
            <div className="flex flex-wrap gap-2 mt-2">
              {photos.map((p, i) => (
                <div key={p} className="relative">
                  <img src={p} alt="" className="w-14 h-14 rounded-lg object-cover" />
                  <button data-testid={`review-photo-remove-${i}`} onClick={() => setPhotos((a) => a.filter((_, idx) => idx !== i))} className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-600 text-white flex items-center justify-center"><X size={12} /></button>
                </div>
              ))}
              {photos.length < 4 && (
                <button data-testid="review-add-photo" onClick={() => fileRef.current?.click()} className="w-14 h-14 rounded-lg border-[1.5px] border-dashed flex flex-col items-center justify-center gap-0.5" style={{ borderColor: BRAND, color: BRAND }}>
                  <Camera size={18} /><span className="text-[9px] font-semibold">Photo</span>
                </button>
              )}
              <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={pickPhotos} />
            </div>
            <button data-testid="review-submit" disabled={busy} onClick={submit} className="mt-3 px-5 py-2 disabled:opacity-50 text-white rounded-xl font-bold text-sm transition-colors" style={{ backgroundColor: BRAND }}>{user ? (busy ? "…" : "Submit Review") : "Login to review"}</button>
          </div>
          <div className="space-y-3">
            {!data.reviews.length && <p className="text-slate-400 text-sm">Abhi koi review nahi — pehle aap likhein!</p>}
            {data.reviews.map((r) => (
              <div key={r.id} data-testid={`review-item-${r.id}`} className="bg-white border border-slate-100 rounded-2xl p-4">
                <div className="flex items-center gap-2">
                  <span className="flex items-center gap-0.5">{[1, 2, 3, 4, 5].map((i) => <Star key={i} size={13} className={i <= r.rating ? "fill-amber-400 text-amber-400" : "text-slate-200"} />)}</span>
                  <span className="text-xs font-bold text-slate-700">{r.user_name}</span>
                  <span className="text-[11px] text-slate-400 ml-auto">{new Date(r.created_at).toLocaleDateString("en-IN")}</span>
                </div>
                {r.title && <p className="font-bold text-sm mt-1">{r.title}</p>}
                {r.comment && <p className="text-sm text-slate-600 mt-0.5">{r.comment}</p>}
                {Array.isArray(r.photos) && r.photos.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2" data-testid={`review-photos-${r.id}`}>
                    {r.photos.map((ph, i) => (
                      <img key={`${r.id}-${i}`} src={ph} alt="" onClick={() => setLightbox(ph)} className="w-16 h-16 rounded-lg object-cover cursor-pointer hover:opacity-90" />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {lightbox && (
        <div className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-4" data-testid="review-lightbox" onClick={() => setLightbox(null)}>
          <button className="absolute top-4 right-4 text-white"><X size={28} /></button>
          <img src={lightbox} alt="" className="max-w-full max-h-[90vh] rounded-xl" />
        </div>
      )}
    </section>
  );
}
